import { Device } from 'mediasoup-client';
import io from 'socket.io-client';
export class MediaSoupClient {
    constructor(onNewStream, onRemoveStream, onUserCountChanged, onChatMessage, authToken // ADDED
    ) {
        this.device = null;
        this._socket = null;
        this.sendTransport = null;
        this.recvTransport = null;
        this.producers = new Map();
        this.consumers = new Map();
        this.localCameraStream = null;
        this.currentRoomId = null;
        this.authToken = null; // ADDED
        this.statsInterval = null; // Stats collection timer
        console.log('ðŸ”Œ Creating socket.io connection...');
        // Set callbacks from constructor parameters
        this.onNewStream = onNewStream;
        this.onRemoveStream = onRemoveStream;
        this.onUserCountChanged = onUserCountChanged;
        this.onChatMessage = onChatMessage;
        this.authToken = authToken || null; // ADDED
        // Initialize socket immediately so App.tsx can set up listeners before joining
        this.initializeSocket();
    }
    // Initialize socket connection (called from joinRoom)
    initializeSocket() {
        if (this._socket && this._socket.connected) {
            console.log('âœ… Socket already connected');
            return;
        }
        // Check if socket exists but not connected (prevent duplicate creation)
        if (this._socket) {
            console.log('âš ï¸ Socket exists but not connected - reusing existing socket');
            return;
        }
        console.log('ðŸ”Œ Creating new Socket.IO connection with auth token...');
        // UPDATED: Pass auth token to Socket.IO
        this._socket = io({
            forceNew: false,
            reconnection: true,
            timeout: 5000,
            transports: ['websocket', 'polling'],
            auth: {
                token: this.authToken // ADDED: Pass JWT token for authentication
            }
        });
        this._socket.on('connect', () => {
            if (this._socket) {
                console.log(`âœ… Socket connected with ID: ${this._socket.id}`);
            }
        });
        this._socket.on('disconnect', (reason) => {
            console.log(`ðŸ”Œ Socket disconnected: ${reason}`);
        });
        // MediaSoup events
        this._socket.on('rtp-capabilities', (rtpCapabilities) => {
            console.log('ðŸ“¡ Received RTP capabilities from server');
            this.loadDevice(rtpCapabilities);
        });
        this._socket.on('new-producer', (data) => {
            console.log('ðŸŽ¥ New producer:', data);
            if (data.peerId !== this._socket?.id) {
                this.consume(data.producerId, data.peerId, data.streamType || 'camera');
            }
        });
        this._socket.on('producer-closed', (data) => {
            console.log('ðŸ”’ Producer closed:', data.peerId);
            this.onRemoveStream(data.peerId);
        });
    }
    // Load MediaSoup device
    async loadDevice(rtpCapabilities) {
        try {
            this.device = new Device();
            await this.device.load({ routerRtpCapabilities: rtpCapabilities });
            console.log('âœ… MediaSoup device loaded successfully');
            await this.createTransports();
        }
        catch (error) {
            console.error('âŒ Device loading failed:', error);
        }
    }
    // Create WebRTC transports
    async createTransports() {
        console.log('ðŸš› Creating send transport...');
        const sendTransportData = await this.sendRequest('createWebRtcTransport', { direction: 'send' });
        console.log('ðŸš› Creating receive transport...');
        const recvTransportData = await this.sendRequest('createWebRtcTransport', { direction: 'recv' });
        this.sendTransport = this.device.createSendTransport(sendTransportData);
        this.recvTransport = this.device.createRecvTransport(recvTransportData);
        console.log('âœ… Both transports created successfully');
        this.setupTransportEvents();
        // Start collecting WebRTC stats
        this.startStatsCollection();
    }
    // Setup transport event handlers
    setupTransportEvents() {
        if (this.sendTransport) {
            this.sendTransport.on('connect', async ({ dtlsParameters }, callback) => {
                try {
                    await this.sendRequest('connectTransport', {
                        direction: 'send',
                        dtlsParameters
                    });
                    callback();
                }
                catch (error) {
                    callback(error);
                }
            });
            this.sendTransport.on('produce', async (parameters, callback) => {
                try {
                    const response = await this.sendRequest('produce', {
                        transportId: this.sendTransport?.id,
                        kind: parameters.kind,
                        rtpParameters: parameters.rtpParameters
                    });
                    callback({ id: response.id });
                }
                catch (error) {
                    callback({ error: error.message });
                }
            });
        }
        if (this.recvTransport) {
            this.recvTransport.on('connect', async ({ dtlsParameters }, callback) => {
                try {
                    await this.sendRequest('connectTransport', {
                        direction: 'recv',
                        dtlsParameters
                    });
                    callback();
                }
                catch (error) {
                    callback(error);
                }
            });
        }
    }
    // Join room
    async joinRoom(roomId) {
        // Prevent duplicate joins to same room
        if (this.currentRoomId === roomId) {
            console.log('Already in room, skipping duplicate join');
            return Promise.resolve({ success: true });
        }
        this.initializeSocket();
        return new Promise((resolve) => {
            const timeout = setTimeout(() => {
                console.log('âš¡ Join room timeout - resolving anyway');
                resolve({ success: true, timeout: true });
            }, 5000);
            this._socket.emit('join-room', roomId, (response) => {
                clearTimeout(timeout);
                this.currentRoomId = roomId;
                resolve(response);
            });
        });
    }
    // Enable camera - matches App.tsx expected interface
    async enableCamera() {
        try {
            console.log('ðŸ“· Enabling camera...');
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { width: 640, height: 480 },
                audio: true
            });
            console.log('ðŸ“· Camera stream acquired:', stream);
            this.localCameraStream = stream;
            console.log('ðŸ“¤ Creating camera video producer...');
            const videoTrack = stream.getVideoTracks()[0];
            const videoProducer = await this.sendTransport.produce({ track: videoTrack });
            this.producers.set('camera-video', videoProducer);
            console.log('âœ… Video producer created:', videoProducer.id);
            console.log('ðŸ“¤ Creating camera audio producer...');
            const audioTrack = stream.getAudioTracks()[0];
            const audioProducer = await this.sendTransport.produce({ track: audioTrack });
            this.producers.set('camera-audio', audioProducer);
            console.log('âœ… Audio producer created:', audioProducer.id);
            console.log('âœ… Camera enabled with producers');
            console.log('ðŸ“¹ Returning MediaStream to App.tsx for local video display');
            return stream;
        }
        catch (error) {
            console.error('âŒ Camera enable failed:', error);
            throw error;
        }
    }
    // Disable camera
    async disableCamera() {
        try {
            console.log('ðŸ“· Disabling camera...');
            // Close video producer
            const videoProducer = this.producers.get('camera-video');
            if (videoProducer) {
                // Tell server to close producer FIRST
                this.socket.emit('close-producer', { producerId: videoProducer.id });
                // Then close locally
                videoProducer.close();
                this.producers.delete('camera-video');
                console.log('âœ… Video producer closed');
            }
            // Close audio producer
            const audioProducer = this.producers.get('camera-audio');
            if (audioProducer) {
                // Tell server to close producer FIRST
                this.socket.emit('close-producer', { producerId: audioProducer.id });
                // Then close locally
                audioProducer.close();
                this.producers.delete('camera-audio');
                console.log('âœ… Audio producer closed');
            }
            // Stop local stream
            if (this.localCameraStream) {
                this.localCameraStream.getTracks().forEach(track => track.stop());
                this.localCameraStream = null;
                console.log('âœ… Local camera stream stopped');
            }
        }
        catch (error) {
            console.error('âŒ Camera disable failed:', error);
            throw error;
        }
    }
    // FIXED: Simplified consume method that eliminates SSRC conflicts
    async consume(producerId, peerId, streamType) {
        try {
            console.log(`ðŸŽ¯ Starting consume for ${streamType} from ${peerId}`);
            // CRITICAL: Prevent invalid peerId
            if (!peerId || peerId === 'undefined') {
                console.error('âŒ Invalid peerId, skipping consume');
                return;
            }
            if (!this.recvTransport || !this.device) {
                console.error('âŒ Transport or device not ready');
                return;
            }
            // Request consumer from server
            const response = await this.sendRequest('consume', {
                transportId: this.recvTransport.id,
                producerId,
                rtpCapabilities: this.device.rtpCapabilities
            });
            const { rtpParameters, id: consumerId } = response;
            console.log(`ðŸ“¡ Consume response for ${streamType} from ${peerId}:`, { consumerId, producerId });
            // Determine consumer kind from RTP parameters
            const isAudioCodec = rtpParameters.codecs.some((codec) => codec.mimeType.toLowerCase().includes('audio') || codec.mimeType.includes('opus'));
            const consumerKind = isAudioCodec ? 'audio' : 'video';
            console.log(`ðŸŽ¯ Creating ${consumerKind} consumer for producer ${producerId}`);
            // Create consumer
            const consumer = await this.recvTransport.consume({
                id: consumerId,
                producerId,
                kind: consumerKind,
                rtpParameters
            });
            console.log(`âœ… Consumer created for ${consumerKind} from ${peerId}:`, consumer.id);
            // CRITICAL FIX: Resume consumer immediately after creation
            try {
                await this.sendRequest('resumeConsumer', {
                    consumerId: consumer.id
                });
                console.log(`ðŸ”„ Consumer resumed for ${consumerKind} from ${peerId}`);
            }
            catch (resumeError) {
                // Fallback with alternate parameter format
                try {
                    await this.sendRequest('resumeConsumer', {
                        id: consumer.id
                    });
                    console.log(`ðŸ”„ Consumer resumed (fallback) for ${consumerKind} from ${peerId}`);
                }
                catch (altError) {
                    console.error(`âŒ Consumer resume failed:`, altError);
                }
            }
            // FIXED: Create individual MediaStream per consumer to prevent SSRC conflicts
            const mediaStream = new MediaStream([consumer.track]);
            // Don't try to set mediaStream.id - it's read-only and causes the error
            console.log(`ðŸ“¡ Created MediaStream for ${peerId}:`, {
                tracks: mediaStream.getTracks().length,
                trackKind: consumer.track.kind,
                trackEnabled: consumer.track.enabled,
                trackReadyState: consumer.track.readyState
            });
            // Store consumer
            this.consumers.set(`${peerId}-${consumerKind}`, consumer);
            // CRITICAL FIX: Call onNewStream callback with correct parameters to match App.tsx
            this.onNewStream(mediaStream, peerId, 'camera');
        }
        catch (error) {
            console.error(`âŒ Failed to consume ${streamType} from ${peerId}:`, error);
        }
    }
    // Socket request helper
    sendRequest(event, data) {
        return new Promise((resolve, reject) => {
            if (!this._socket || !this._socket.connected) {
                resolve({ error: 'Socket not connected' });
                return;
            }
            const timeout = setTimeout(() => {
                console.log(`âš¡ Request timeout: ${event} - resolving with default`);
                resolve({ success: false, timeout: true });
            }, 3000);
            this._socket.emit(event, data, (response) => {
                clearTimeout(timeout);
                if (response && response.error) {
                    console.error(`âŒ Socket request failed (${event}):`, response.error);
                    reject(new Error(response.error));
                }
                else {
                    resolve(response);
                }
            });
        });
    }
    // Get socket instance for external access (used by App.tsx)
    get socket() {
        return this._socket;
    }
    // Collect and send WebRTC statistics
    async collectAndSendStats() {
        try {
            if (!this.sendTransport || !this._socket) {
                return;
            }
            // Get stats from send transport
            const stats = await this.sendTransport.getStats();
            let totalLatency = 0;
            let totalPacketLoss = 0;
            let totalJitter = 0;
            let totalBitrate = 0;
            let statsCount = 0;
            let frameRate = 0;
            let resolution = '';
            let codec = '';
            let candidateType = '';
            let bytesTransferred = 0;
            // Parse RTCStats reports
            stats.forEach((report) => {
                if (report.type === 'outbound-rtp') {
                    // Calculate packet loss
                    if (report.packetsSent && report.packetsLost) {
                        const packetLoss = (report.packetsLost / report.packetsSent) * 100;
                        totalPacketLoss += packetLoss;
                    }
                    // Get bitrate (bytes per second to kbps)
                    if (report.bytesSent && report.timestamp) {
                        totalBitrate += (report.bytesSent * 8) / 1000; // Convert to kbps
                        bytesTransferred = report.bytesSent;
                    }
                    // Get frame rate (only for video)
                    if (report.kind === 'video' && report.framesPerSecond) {
                        frameRate = report.framesPerSecond;
                    }
                    // Get resolution (only for video)
                    if (report.kind === 'video' && report.frameWidth && report.frameHeight) {
                        resolution = `${report.frameWidth}x${report.frameHeight}`;
                    }
                    statsCount++;
                }
                if (report.type === 'candidate-pair' && report.state === 'succeeded') {
                    // Get RTT (latency) in milliseconds
                    if (report.currentRoundTripTime) {
                        totalLatency += report.currentRoundTripTime * 1000; // Convert to ms
                    }
                    statsCount++;
                }
                if (report.type === 'inbound-rtp') {
                    // Get jitter
                    if (report.jitter) {
                        totalJitter += report.jitter * 1000; // Convert to ms
                    }
                }
                // Get ICE candidate type
                if (report.type === 'local-candidate' && report.candidateType) {
                    candidateType = report.candidateType;
                }
                // Get codec information
                if (report.type === 'codec' && report.mimeType) {
                    codec = report.mimeType.split('/')[1]?.toUpperCase() || '';
                }
            });
            // Get network type (if available)
            let networkType = 'unknown';
            if ('connection' in navigator && navigator.connection) {
                const conn = navigator.connection;
                networkType = conn.type || 'unknown'; // wifi, ethernet, cellular, bluetooth
            }
            // Calculate averages
            const connectionStats = {
                latency: statsCount > 0 ? Math.round(totalLatency / statsCount) : 0,
                packetLoss: statsCount > 0 ? totalPacketLoss / statsCount : 0,
                jitter: statsCount > 0 ? totalJitter / statsCount : 0,
                bitrate: statsCount > 0 ? totalBitrate / statsCount : 0,
                frameRate: frameRate || undefined,
                resolution: resolution || undefined,
                networkType: networkType,
                codec: codec || undefined,
                candidateType: candidateType || undefined,
                bytesTransferred: bytesTransferred || undefined
            };
            // Send stats to server
            this._socket.emit('update-connection-stats', { stats: connectionStats });
        }
        catch (error) {
            console.error('âŒ Failed to collect WebRTC stats:', error);
        }
    }
    // Start collecting stats periodically
    startStatsCollection() {
        // Clear any existing interval
        if (this.statsInterval) {
            clearInterval(this.statsInterval);
        }
        // Collect stats every 5 seconds
        this.statsInterval = setInterval(() => {
            this.collectAndSendStats();
        }, 5000);
        // Collect initial stats immediately
        this.collectAndSendStats();
    }
    // Stop collecting stats
    stopStatsCollection() {
        if (this.statsInterval) {
            clearInterval(this.statsInterval);
            this.statsInterval = null;
        }
    }
    // Disconnect method for cleanup
    disconnect() {
        console.log('ðŸ§¹ Cleaning up MediaSoup client...');
        // Stop stats collection
        this.stopStatsCollection();
        // Close all producers
        this.producers.forEach((producer, key) => {
            producer.close();
            console.log(`ðŸ”’ Closed producer: ${key}`);
        });
        this.producers.clear();
        // Close all consumers
        this.consumers.forEach((consumer, key) => {
            consumer.close();
            console.log(`ðŸ”’ Closed consumer: ${key}`);
        });
        this.consumers.clear();
        // Close transports
        if (this.sendTransport) {
            this.sendTransport.close();
            this.sendTransport = null;
        }
        if (this.recvTransport) {
            this.recvTransport.close();
            this.recvTransport = null;
        }
        // Stop local stream
        if (this.localCameraStream) {
            this.localCameraStream.getTracks().forEach(track => track.stop());
            this.localCameraStream = null;
        }
        // Disconnect socket
        if (this._socket) {
            this._socket.disconnect();
            this._socket = null;
        }
        this.currentRoomId = null;
    }
}
